import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { SiteHeader } from '../../components/SiteLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft, MagnifyingGlass, EnvelopeSimple, Buildings, Phone, Globe,
  Clock, CheckCircle,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INQUIRY_TYPES = ['Brand', 'Retailer', 'Manufacturer', 'Investor', 'Partnership', 'Other'];

const SLATE = '#0B1426';
const MUTED = '#5B6577';
const HAIRLINE = '#ECECEC';
const CORAL = '#E63946';
const IVORY = '#FBF8F1';

const STATUS_META = {
  new:         { label: 'New',         color: '#9B1C1C', bg: '#FDE8E8' },
  in_progress: { label: 'In Progress', color: '#723B13', bg: '#FEF3C7' },
  closed:      { label: 'Closed',      color: '#03543F', bg: '#DEF7EC' },
};

export default function InquiriesPage() {
  const [data, setData] = useState({ items: [], counts: { new: 0, in_progress: 0, closed: 0, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.inquiry_type = typeFilter;
      const r = await axios.get(`${API_URL}/api/inquiries`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) {
      console.error('inquiries fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, typeFilter]);

  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  const openDetail = (inq) => { setSelected(inq); setDetailOpen(true); };
  const onUpdated = (updated) => {
    setSelected(updated);
    setData((d) => ({
      ...d,
      items: d.items.map((it) => (it.id === updated.id ? updated : it)),
    }));
    fetchData();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }} data-testid="admin-inquiries-page">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/admin"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] mb-6 transition-colors"
          style={{ color: MUTED }}
          onMouseEnter={(e) => { e.currentTarget.style.color = SLATE; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
          data-testid="back-to-admin"
        >
          <ArrowLeft size={12} weight="bold" /> Control room
        </Link>
        <div className="mb-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>
            Partnership Inquiries
          </p>
          <h1
            className="font-light leading-tight tracking-tight"
            style={{
              color: SLATE,
              fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
              fontSize: 'clamp(2rem, 4vw, 2.75rem)',
            }}
          >
            Inbound enterprise inquiries.
          </h1>
          <p className="text-[13.5px] leading-relaxed mt-3 max-w-2xl" style={{ color: MUTED }}>
            Brand, retailer, manufacturer, investor, and partnership inquiries —
            received via the ToxIntel landing page and routed to The Agyekum Group team.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px mb-8" style={{ backgroundColor: HAIRLINE }}>
          <Stat label="Total" value={data.counts.total} />
          <Stat label="New" value={data.counts.new} accent />
          <Stat label="In Progress" value={data.counts.in_progress} />
          <Stat label="Closed" value={data.counts.closed} />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
            <Input
              placeholder="Search by name, company, email, message…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="h-10 pl-9 rounded-sm"
              data-testid="inquiries-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-44 h-10 rounded-sm" data-testid="inquiries-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-44 h-10 rounded-sm" data-testid="inquiries-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {INQUIRY_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="rounded-sm overflow-hidden" style={{ border: `1px solid ${HAIRLINE}` }}>
          {loading ? (
            <div className="px-6 py-10 text-center text-[13px]" style={{ color: MUTED }}>Loading…</div>
          ) : data.items.length === 0 ? (
            <div className="px-6 py-14 text-center" data-testid="inquiries-empty">
              <p className="text-[14px]" style={{ color: SLATE }}>No inquiries match the current filters.</p>
            </div>
          ) : (
            <ul style={{ backgroundColor: '#FFFFFF' }}>
              {data.items.map((inq, idx) => (
                <InquiryRow
                  key={inq.id}
                  inq={inq}
                  isLast={idx === data.items.length - 1}
                  onClick={() => openDetail(inq)}
                />
              ))}
            </ul>
          )}
        </div>
      </main>
      <InquiryDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        inquiry={selected}
        onUpdated={onUpdated}
      />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      className="px-5 py-4"
      style={{ backgroundColor: '#FFFFFF' }}
      data-testid={`inquiries-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: MUTED }}>{label}</p>
      <p
        className="text-3xl tabular-nums leading-none mt-2"
        style={{
          color: accent && value > 0 ? CORAL : SLATE,
          fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
          fontWeight: 300,
        }}
      >
        {value}
      </p>
    </div>
  );
}

function InquiryRow({ inq, isLast, onClick }) {
  const meta = STATUS_META[inq.status] || STATUS_META.new;
  const when = new Date(inq.created_at).toLocaleString();
  return (
    <li
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className="flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors"
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${HAIRLINE}`,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = IVORY; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
      data-testid={`inquiry-row-${inq.submission_id}`}
    >
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider mt-0.5 flex-shrink-0"
        style={{ color: meta.color, backgroundColor: meta.bg }}
      >
        {meta.label}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <p className="text-[15px] font-semibold truncate" style={{ color: SLATE }}>{inq.company}</p>
          <span className="text-[12px]" style={{ color: MUTED }}>·</span>
          <p className="text-[13px]" style={{ color: MUTED }}>{inq.name}</p>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm"
            style={{ color: SLATE, border: `1px solid ${HAIRLINE}` }}
          >
            {inq.inquiry_type}
          </span>
        </div>
        <p className="text-[13px] mt-1 line-clamp-2" style={{ color: SLATE }}>{inq.message}</p>
        <p className="text-[10px] font-mono uppercase tracking-wider mt-2" style={{ color: MUTED }}>
          {inq.submission_id} · {when}
        </p>
      </div>
    </li>
  );
}

function InquiryDetailDialog({ open, onOpenChange, inquiry, onUpdated }) {
  const [status, setStatus] = useState('new');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (inquiry) {
      setStatus(inquiry.status || 'new');
      setNotes(inquiry.admin_notes || '');
    }
  }, [inquiry]);

  const save = async () => {
    if (!inquiry) return;
    setSaving(true);
    try {
      const r = await axios.patch(
        `${API_URL}/api/inquiries/${inquiry.id}`,
        { status, admin_notes: notes },
        { withCredentials: true },
      );
      onUpdated(r.data);
    } catch (e) {
      console.error('inquiry update failed', e);
    } finally {
      setSaving(false);
    }
  };

  if (!inquiry) return null;
  const meta = STATUS_META[inquiry.status] || STATUS_META.new;
  const when = new Date(inquiry.created_at).toLocaleString();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0 rounded-lg"
        style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
        data-testid="inquiry-detail-dialog"
      >
        <DialogHeader className="px-7 pt-7 pb-5" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
          <div className="flex items-center gap-3 mb-2">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider"
              style={{ color: meta.color, backgroundColor: meta.bg }}
            >
              {meta.label}
            </span>
            <span
              className="text-[10px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm"
              style={{ color: SLATE, border: `1px solid ${HAIRLINE}` }}
            >
              {inquiry.inquiry_type}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: MUTED }}>
              {inquiry.submission_id}
            </span>
          </div>
          <DialogTitle
            className="font-light tracking-tight"
            style={{
              color: SLATE,
              fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
              fontSize: '1.6rem',
            }}
          >
            {inquiry.company}
          </DialogTitle>
          <p className="text-[12px] mt-1" style={{ color: MUTED }}>
            <Clock size={11} className="inline mr-1" /> Received {when}
          </p>
        </DialogHeader>

        <div className="px-7 py-6 space-y-5">
          <DetailRow icon={Buildings} label="Contact" value={inquiry.name} />
          <DetailRow icon={EnvelopeSimple} label="Email" value={
            <a href={`mailto:${inquiry.email}`} className="underline" style={{ color: SLATE, textUnderlineOffset: 3 }}>
              {inquiry.email}
            </a>
          } />
          {inquiry.phone && <DetailRow icon={Phone} label="Phone" value={inquiry.phone} />}
          {inquiry.website && (
            <DetailRow icon={Globe} label="Website" value={
              <a href={inquiry.website.startsWith('http') ? inquiry.website : `https://${inquiry.website}`}
                target="_blank" rel="noopener noreferrer"
                className="underline" style={{ color: SLATE, textUnderlineOffset: 3 }}>
                {inquiry.website}
              </a>
            } />
          )}
          {inquiry.source_page && (
            <DetailRow label="Source" value={
              <span className="text-[12px] font-mono" style={{ color: MUTED }}>{inquiry.source_page}</span>
            } />
          )}

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: MUTED }}>
              Message
            </Label>
            <div
              className="mt-2 p-4 rounded-sm whitespace-pre-wrap text-[14px] leading-relaxed"
              style={{ color: SLATE, backgroundColor: IVORY, border: `1px solid ${HAIRLINE}` }}
              data-testid="inquiry-detail-message"
            >
              {inquiry.message}
            </div>
          </div>

          <div className="pt-3" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>
              Admin actions
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              {Object.entries(STATUS_META).map(([key, sm]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatus(key)}
                  className="px-3 py-2 rounded-sm text-[11px] font-bold uppercase tracking-wider transition-all"
                  style={{
                    backgroundColor: status === key ? sm.bg : '#FFFFFF',
                    color: status === key ? sm.color : SLATE,
                    border: `1px solid ${status === key ? sm.color : HAIRLINE}`,
                  }}
                  data-testid={`status-btn-${key}`}
                >
                  {sm.label}
                </button>
              ))}
            </div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: MUTED }}>
              Internal notes
            </Label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Followup history, next steps, owner…"
              className="mt-1.5 rounded-sm resize-none"
              data-testid="inquiry-notes"
            />
            <div className="flex justify-end mt-4">
              <Button
                onClick={save} disabled={saving}
                className="h-10 px-6 rounded-full text-[11px] font-bold uppercase tracking-[0.22em]"
                style={{ backgroundColor: SLATE, color: '#FFFFFF' }}
                data-testid="inquiry-save"
              >
                {saving ? 'Saving…' : <><CheckCircle size={13} className="mr-1.5" /> Save changes</>}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="w-24 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] flex-shrink-0" style={{ color: MUTED }}>
        {Icon && <Icon size={11} weight="duotone" />}
        {label}
      </span>
      <div className="flex-1 text-[14px]" style={{ color: SLATE }}>{value}</div>
    </div>
  );
}
